import { get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import * as validators from './validations';
/**
 * Validates a record against a set of validations
 * @param record The Record to validate
 * @param validations The validations to run on the record
 * @param config The config for the validation process
 * @returns
 */
export const validate = (record, validations, config = {}) => {
    const { validateUndefined } = config;
    const _validations = (typeof validations === 'function' ? validations(record) : validations);
    const errors = {};
    Object.entries(_validations || {}).forEach(([key, _validation]) => {
        let err = false;
        if (_validation) {
            const value = get(record, key);
            const isUndefined = value === undefined || value === null;
            if (isUndefined && !validateUndefined) {
                err = false;
            }
            else {
                const validated = _validation(validators, value);
                err = (validateUndefined ?? !isUndefined) && validated.filter(Boolean)[0];
            }
        }
        if (err) {
            errors[key] = err;
        }
    });
    return errors;
};
/**
 * Creates a validator hook for a record
 * @param validations The validations to run on the record
 * @returns
 * @example
 * const useValidator = makeRecordValidator({
 *   name: (api, value) => [api.required(value), api.minLength(value, 3)],
 *   email: (api, value) => [api.required(value), api.email(value)],
 * });
 *
 * const { validate, errors, setErrors } = useValidator();
 *
 * const handleSubmit = () => {
 *   validate(record);
 * };
 *
 * const handleChange = () => {
 *   validate(record);
 * };
 *
 */
export const makeValidatorHook = (validations, _config) => (record) => {
    const [config, setValidationConfig] = useState({
        validateUndefined: false,
        validateOnChange: true,
        ..._config,
    });
    const [validationErrors, setValidationErrors] = useState({});
    const { validateUndefined } = config;
    const validateRecord = useCallback((record, options = {}) => {
        const { setErrorsState = false, ...config } = options;
        const errors = validate(record, validations ?? {}, { validateUndefined, ...config });
        if (setErrorsState)
            setValidationErrors(errors);
        return errors;
    }, [validations, validateUndefined]);
    useEffect(() => {
        if (record && config.validateOnChange) {
            setValidationErrors(validateRecord(record));
        }
    }, [record, validateRecord, config.validateOnChange]);
    return {
        validate: validateRecord,
        validationErrors,
        setValidationErrors,
        setValidationConfig,
    };
};
